import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import './Legal.css';
import AppHeader from '../../components/AppHeader';
import Footer from '../../components/Footer/Footer';

function PrivacyPolicy() {
  const navigate = useNavigate();
  const lastUpdated = 'December 1, 2025';

  return (
    <div className="legal-page">
      <AppHeader showActions={false} />

      <div className="page-back-nav">
        <button
          className="back-to-home-btn"
          onClick={() => navigate('/loyalty-directory')}
          aria-label="Back to Home"
        >
          <FaArrowLeft />
          <span>Back to Home</span>
        </button>
      </div>

      <div className="legal-container">
        <header className="legal-header">
          <h1>Privacy Policy</h1>
          <p className="legal-last-updated">Last Updated: {lastUpdated}</p>
        </header>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Block Club ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our loyalty program platform and related services (collectively, the "Services").
            </p>
            <p>
              By accessing or using our Services, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, please do not use our Services.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Personal Information</h3>
            <p>We collect several types of information from and about users of our Services, including:</p>
            <ul>
              <li><strong>Contact Information:</strong> Email address, name, phone number</li>
              <li><strong>Account Information:</strong> Username, password, and other account credentials</li>
              <li><strong>Transaction Data:</strong> Information about coupons redeemed, offers saved, and venue interactions</li>
              <li><strong>Preference Data:</strong> Your dining preferences, dietary restrictions, favorite venues, and other personalization settings</li>
              <li><strong>Company Affiliation:</strong> Your organization or company membership for loyalty program access</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you access our Services, we automatically collect certain information, including:</p>
            <ul>
              <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, links clicked, and other diagnostic data</li>
              <li><strong>Location Data:</strong> General geographic location based on IP address</li>
              <li><strong>Cookies and Tracking Technologies:</strong> Information collected through cookies, web beacons, and similar technologies (see our Cookie Policy for details)</li>
            </ul>

            <h3>2.3 Information from Third Parties</h3>
            <p>We may receive information about you from:</p>
            <ul>
              <li>Participating venues and merchants</li>
              <li>Your employer or organization that provides loyalty program access</li>
              <li>Third-party authentication services</li>
              <li>Analytics and service providers</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect for various purposes, including:</p>
            <ul>
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve our Services</li>
              <li><strong>Account Management:</strong> To create and manage your account, verify your identity, and process your requests</li>
              <li><strong>Personalization:</strong> To personalize your experience and provide tailored offers and recommendations</li>
              <li><strong>Communication:</strong> To send you updates, newsletters, marketing materials, and other information (with your consent)</li>
              <li><strong>Transaction Processing:</strong> To process coupon redemptions and track loyalty program participation</li>
              <li><strong>Analytics:</strong> To analyze usage patterns and improve our Services</li>
              <li><strong>Security:</strong> To protect against fraud, unauthorized access, and other security threats</li>
              <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our Terms of Service</li>
              <li><strong>Customer Support:</strong> To respond to your inquiries and provide customer support</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Legal Basis for Processing (GDPR)</h2>
            <p>If you are located in the European Economic Area (EEA), our legal basis for collecting and using your personal information includes:</p>
            <ul>
              <li><strong>Consent:</strong> You have given clear consent for us to process your personal data for specific purposes</li>
              <li><strong>Contract:</strong> Processing is necessary to perform a contract with you</li>
              <li><strong>Legal Obligation:</strong> Processing is necessary to comply with legal requirements</li>
              <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate interests, provided your rights do not override these interests</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. How We Share Your Information</h2>
            <p>We may share your information in the following circumstances:</p>

            <h3>5.1 Participating Venues</h3>
            <p>We share necessary information with participating restaurants and merchants to facilitate coupon redemption and loyalty program benefits.</p>

            <h3>5.2 Service Providers</h3>
            <p>We engage third-party companies and individuals to perform services on our behalf, including:</p>
            <ul>
              <li>Email delivery services</li>
              <li>Analytics providers</li>
              <li>Payment processors</li>
              <li>Cloud storage providers</li>
              <li>Customer support tools</li>
            </ul>

            <h3>5.3 Business Transfers</h3>
            <p>If we are involved in a merger, acquisition, or asset sale, your personal information may be transferred as part of that transaction.</p>

            <h3>5.4 Legal Requirements</h3>
            <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities.</p>

            <h3>5.5 Consent</h3>
            <p>We may share your information for any other purpose with your consent.</p>

            <h3>5.6 Event Hosts, Venues, and Promotional Partners</h3>
            <p>When you purchase tickets for or register for an event through our Services, we may share limited personal information with the venue, host, or promotional partner associated with that event, but only as reasonably necessary to operate, support, or promote that specific event, provide related customer service, or fulfill obligations connected to your purchase or registration.</p>
          </section>

          <section className="legal-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>
            <p>
              When determining the retention period, we consider the amount, nature, and sensitivity of the personal data, the potential risk of harm from unauthorized use or disclosure, the purposes for processing, and applicable legal requirements.
            </p>
            <p>
              You may request deletion of your account and personal data at any time by contacting us at support@blockclub.com.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Your Privacy Rights</h2>

            <h3>7.1 General Rights</h3>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete personal information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Restriction:</strong> Request restriction of processing of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your personal information to another service</li>
              <li><strong>Objection:</strong> Object to processing of your personal information</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time where we rely on consent</li>
            </ul>

            <h3>7.2 California Privacy Rights (CCPA)</h3>
            <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
            <ul>
              <li>Right to know what personal information is collected, used, shared, or sold</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>Right to non-discrimination for exercising your CCPA rights</li>
            </ul>

            <h3>7.3 How to Exercise Your Rights</h3>
            <p>
              To exercise any of these rights, please contact us at support@blockclub.com or through the contact form in our Services. We will respond to your request within the timeframe required by applicable law (typically 30 days).
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Security of Your Information</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized or unlawful processing, accidental loss, destruction, or damage. These measures include:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. If we become aware that we have collected personal information from children without verification of parental consent, we will take steps to remove that information from our servers.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ from those in your jurisdiction.
            </p>
            <p>
              If you are located outside the United States and choose to provide information to us, please note that we transfer the data, including personal information, to the United States and process it there.
            </p>
            <p>
              For transfers from the EEA to countries not deemed to provide an adequate level of protection, we implement appropriate safeguards, such as Standard Contractual Clauses approved by the European Commission.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Third-Party Links</h2>
            <p>
              Our Services may contain links to third-party websites, plug-ins, and applications. Clicking on those links or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements.
            </p>
            <p>
              When you leave our Services, we encourage you to read the privacy policy of every website you visit.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this policy.
            </p>
            <p>
              For material changes, we will provide additional notice (such as adding a statement to our homepage or sending you an email notification).
            </p>
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> support@blockclub.com</p>
              <p><strong>Address:</strong> Block Club, St. Louis, MO</p>
              <p><strong>Data Protection Officer:</strong> privacy@blockclub.com</p>
            </div>
            <p>
              We will respond to all legitimate requests within one month. Occasionally it may take us longer if your request is particularly complex or you have made multiple requests. In this case, we will notify you and keep you updated.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Additional Information for EEA Residents</h2>
            <p>
              If you are located in the EEA, you have the right to lodge a complaint with a supervisory authority if you believe our processing of your personal data violates applicable law.
            </p>
            <p>
              You can find your local supervisory authority contact information at: <a href="https://edpb.europa.eu/about-edpb/board/members_en" target="_blank" rel="noopener noreferrer">https://edpb.europa.eu/about-edpb/board/members_en</a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
